import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useLanguage } from "../../../context/LanguageProvider";
import { resolvePublicImageUrl, SITE_URL } from "../../../shared/utils/apiConfig";
import StatusMessage from "../../../shared/components/elements/StatusMessage";
import ResponsiveImage from "../../../shared/components/elements/ResponsiveImage";
import PageLayout from "../../../shared/components/layout/PageLayout";
import SEO from "../../../shared/components/layout/SEO";
import WrapperBlog from "../../../shared/components/layout/WrapperBlog";

import BlogInfoSidebar from "../components/BlogInfoSidebar";
import BlogTableOfContents from "../components/BlogTableOfContents";
import { useBlogPost } from "../hooks/useBlogPost";
import { prepareBlogContent } from "../utils/blogContent";

const POST_MAIN_CLASS = "w-full flex-1 my-10 md:my-16 mb-5";

const BlogPostPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const { lang, dir, t } = useLanguage();
    const { post, loading, error } = useBlogPost(slug);

    // Sanitized HTML plus table of contents, recomputed only when the post changes.
    // Declared before the early returns below, because hooks can't be called conditionally.
    const content = useMemo(
        () => prepareBlogContent(post?.content ?? ""),
        [post?.content]
    );

    if (loading || error || !post) {
        const message = loading
            ? t.blog.loading
            : error
                ? t.blog.loadFailed
                : t.blog.not_found;

        return (
            <PageLayout mainClassName={POST_MAIN_CLASS}>
                <StatusMessage>{message}</StatusMessage>
            </PageLayout>
        );
    }

    const postUrl = `${SITE_URL}/${lang}/blog/${post.slug}`;

    return (
        <>
            <SEO
                title={post.title}
                description={post.description ?? ""}
                path={`/blog/${post.slug}`}
            />

            <PageLayout mainClassName={POST_MAIN_CLASS}>
                <WrapperBlog className="relative flex flex-col lg:flex-row gap-4 xl:gap-10 justify-between">
                    <BlogTableOfContents items={content.toc} />

                    <article className="flex-1 w-full mb-5 px-4">
                        {post.category && (
                            <div className="rounded-full bg-black text-white inline-block py-2 px-6 text-md mb-5">
                                {post.category}
                            </div>
                        )}

                        <h1 className="font-bold text-2xl sm:text-3xl md:text-4xl mb-8">
                            {post.h1 ?? post.title}
                        </h1>

                        {post.image && (
                            <ResponsiveImage
                                className="w-full max-h-[600px] object-cover rounded-lg mb-8"
                                src={resolvePublicImageUrl(post.image)}
                                alt={post.title}
                            />
                        )}

                        <div
                            id="blogpost"
                            dir={dir}
                            className="text-base text-lg md:text-2xl lg:text-md text-black rounded-lg leading-relaxed space-y-4"
                            dangerouslySetInnerHTML={{ __html: content.html }}
                        />
                    </article>

                    <BlogInfoSidebar post={post} url={postUrl} />
                </WrapperBlog>
            </PageLayout>
        </>
    );
};

export default BlogPostPage;